<template>
  <q-modal
    :show="showModal || showNoFiltersModal"
    :hanging-content="filterUpdatedInfo"
    scrollable
    wide
    @close="$emit('close')"
  >
    <template v-if="!loadingModal" #header>
      <div class="flex justify-between">
        <span class="text-lg font-medium text-gray-80">{{
          editModal ? $t('conversations.filters.form.update_heading') : $t('conversations.filters.form.create_heading')
        }}</span>
        <div class="flex items-center">
          <help-circle-icon class="mr-4 font-medium text-gray-60 icon-m" />
          <ui-close-button :rounded="false" :small="false" @click="$emit('close')" />
        </div>
      </div>
    </template>
    <div v-if="loadingModal" class="text-center text-gray-50">
      <p>{{ $t('conversations.filters.form.loading_options_message') }}</p>
      <ui-spinner class="relative min-h-[300px]" />
    </div>
    <template v-else>
      <ui-alert v-if="noFilters" warning class="mb-6">
        <i18n-t keypath="conversations.filters.form.no_filters_message">
          <template #recommendedFilter>
            "<strong>{{ $t('conversations.filters.form.recommended_filter') }}</strong
            >"
          </template>
        </i18n-t>
      </ui-alert>

      <form class="settings-form !mt-5 min-h-[300px]" @submit.prevent="addFilter">
        <div class="flex items-start gap-6">
          <ui-form-field
            ref="nameFieldRef"
            class="w-80"
            :label="$t('conversations.filters.form.name_field_label')"
            :error="!newFilterName && saveClicked ? $t('filter.name_empty') : undefined"
          >
            <ui-input v-model.trim="newFilterName" name="name" required maxlength="255" />
          </ui-form-field>

          <q-toggle
            v-if="!hasWorkspaceRole('AGENT')"
            v-model="newFilterPublicity"
            :label="$t('conversations.filters.form.visibility_toggle_label')"
            :options="[
              { label: $t('conversations.filters.form.public_option_label'), value: true, icon: UnlockIcon },
              { label: $t('conversations.filters.form.private_option_label'), value: false, icon: LockIcon },
            ]"
          />
        </div>

        <ui-alert v-if="warningMessage" warning class="my-6">
          {{ warningMessage }}
        </ui-alert>

        <filter-search-input
          v-model:ai-mode="isAiMode"
          :options="fetchedFilterOptions.defaultFilterOptions"
          :autoqa-fields="fetchedFilterOptions.autoQaFilterOptions"
          :spotlight-fields="fetchedFilterOptions.spotlightFilterOptions"
          :intelligent-fields="fetchedFilterOptions.intelligentFilterOptions"
          :show-divider="showFilterOptions"
          @ai-results="onAiResults"
          @update:search="filterSearch = $event"
          @loading="onAiLoading"
        />

        <filter-options
          v-if="fetchedFilterOptions && !aiLoading && (showFilterOptions || isAiMode)"
          id="add-new-filters"
          v-model="newFilterOptions"
          class="mt-8"
          :edit-modal="editModal"
          is-modal
          :options="fetchedFilterOptions.defaultFilterOptions"
          :autoqa-fields="fetchedFilterOptions.autoQaFilterOptions"
          :spotlight-fields="fetchedFilterOptions.spotlightFilterOptions"
          :custom-category-fields="fetchedFilterOptions.customCategoryFilterOptions"
          :intelligent-filter-fields="fetchedFilterOptions.intelligentFilterOptions"
          :global-search="filterSearch"
          :show-all-conditions="!isAiMode"
          :show-only-date="isAiMode && !hasNonDateOptions"
        />
      </form>
    </template>

    <template v-if="!loadingModal" #actions>
      <div class="mr-auto flex flex-col text-sm text-gray-60">
        <span class="flex flex-row gap-1">
          <i18n-t
            tag="div"
            keypath="conversations.filters.form.ticket_count_label_with_appendix"
            class="flex"
            :plural="filterTicketCount"
          >
            <template #count>
              <ui-spinner v-if="loadingTickets || aiLoading" class="relative ml-4 flex-1" />
              <span v-else class="ml-1 font-medium text-gray-80">
                <template v-if="showTicketCountError">{{ $t('conversations.filters.form.ticket_count_na') }}</template>
                <template v-else-if="filterTicketCount > 999 && !session.features.showExactTicketCount">
                  {{ $n(999, { signDisplay: 'always' }) }}
                </template>
                <template v-else>{{ filterTicketCount }}</template>
              </span>
            </template>
            <template #appendix>
              <template v-if="!loadingTickets && !aiLoading && !hasTimeframeFilter">
                <span class="ml-1">{{ $t('conversations.filters.form.over_last_30_days') }}</span>
                <span
                  v-tippy="{ content: $t('conversations.filters.form.timeframe_tooltip'), delay }"
                  class="ml-1 flex h-5 w-5 self-center rounded-xl bg-gray-10 p-1"
                >
                  <info-icon class="self-center icon-m" />
                </span>
              </template>
            </template>
          </i18n-t>
        </span>
        <a
          :href="$t('support_links.find_conversations_to_review')"
          target="_blank"
          rel="noopener noreferrer"
          class="content-link"
          :data-track-event-name="APPLICATIONS_EVENT_NAMES.FILTER_LEARN_ABOUT_FILTERING_CLICKED"
          :data-track-sub-feature-id="APPLICATIONS_SUB_FEATURE_IDS.FILTERS"
          data-track-click
        >
          {{ $t('conversations.filters.form.read_more') }}
        </a>
      </div>

      <assignment-type-dropdown
        v-if="canConvertToAssignment"
        :filter-name="newFilterName"
        :filter-options="newFilterOptions"
        @close="$emit('close')"
      />
      <ui-button v-if="!noFilters" secondary class="mr-3" @click="$emit('close')">
        {{ $t('universal.cancel') }}
      </ui-button>
      <ui-button type="submit" @click="addFilter">
        {{
          editModal ? $t('conversations.filters.form.update_submit') : $t('conversations.filters.form.create_submit')
        }}
      </ui-button>
    </template>
  </q-modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { HelpCircleIcon, InfoIcon, LockIcon, UnlockIcon } from '@klausapp/ui-kit/icons'
import { UiButton, UiFormField, UiInput, UiSpinner, UiAlert, UiCloseButton } from '@klausapp/ui-kit'
import { v4 as uuid } from 'uuid'

import { formatDateIntl } from '@/utils/date'
import QModal from '@/components/QModal.vue'
import { toast } from '@/components/toast'
import { trackEvent } from '@/tracking'
import {
  APPLICATIONS_EVENT_NAMES,
  APPLICATIONS_SUB_FEATURE_IDS,
  PROMPT_FILTERING_EVENT_NAMES,
  filterContextLabel,
} from '@/tracking/features/applications'
import { session } from '@/composables/useSession'
import i18n from '@/i18n'
import { hasWorkspaceRole } from '@/utils/roleUtils'
import { bus } from '@/utils/bus'
import { delay } from '@/components/Tippy'
import useAutoQaDateWarning from '@/composables/useAutoQaDateWarning'
import useReviewSidebar from '@/composables/useReviewSidebar/useReviewSidebar'
import QToggle from '@/components/QToggle.vue'

import {
  activeFilterId,
  privateFilters,
  publicFilters,
  setActiveFilterId,
} from '../../composables/useConversationFilters'

import {
  constructFilterOptions,
  constructDynamicFilterOptions,
  dateConditions,
  deconstructFilterOptions,
  generateFilterLabels,
  getDayjsRangeFromOption,
  type DeconstructedOption,
} from '../../utils/filters'
import { type BaseFilter, createFilter, editFilter, getFilterDetails, memGetFilterTicketCount } from '../../api/filters'
import useFetchFilterOptions from '../../composables/useFetchFilterOptions'
import FilterOptions from './FilterOptions.vue'
import FilterSearchInput from './FilterSearchInput.vue'
import AssignmentTypeDropdown from './AssignmentTypeDropdown.vue'

const props = defineProps<{
  showModal: boolean
  showNoFiltersModal: boolean
  public: boolean
  promptNewFilter?: boolean
  editableFilter?: BaseFilter
  canConvertToAssignment?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update'): void
  (e: 'stop-ticket-list-loading'): void
}>()

const editModal = ref(false)
const editFilterId = ref(null)
const newFilterPublicity = ref(false)
const newFilterOptions = ref<DeconstructedOption[]>([])
const newFilterName = ref('')
const saveClicked = ref(false)
const filterTicketCount = ref<number>(0)
const showTicketCountError = ref(false)
const loadingModal = ref(false)
const loadingTickets = ref(false)
const filterUpdatedInfo = ref('')
const nameFieldRef = ref(null)
const isAiMode = ref(true)
const filterSearch = ref('')
const hasSubmittedPrompt = ref(false)
const aiLoading = ref(false)
// Snapshot of the AI-returned options, to detect whether the user edited them before saving.
const aiResultsSnapshot = ref<DeconstructedOption[] | null>(null)
// True once an AI-assisted filter has been saved, so the close watcher doesn't also fire DISCARDED.
const aiSaved = ref(false)

const showFilterOptions = computed(
  () => !isAiMode.value || editModal.value || (hasSubmittedPrompt.value && !aiLoading.value),
)

const { data: fetchedFilterOptions } = useFetchFilterOptions()
const { displayAsEmoji } = useReviewSidebar()

const filterContext = filterContextLabel()

// Tracks only user-driven adds — reset to null whenever the modal opens so the freshly
// loaded filter's initial population isn't counted (this modal is never unmounted).
let previousFilterIds: Set<string> | null = null

watch(
  newFilterOptions,
  (filters) => {
    const currentFilterIds = new Set(filters.map((f) => f.id))

    if (previousFilterIds) {
      filters
        .filter((f) => !previousFilterIds?.has(f.id))
        .forEach(() =>
          trackEvent(APPLICATIONS_EVENT_NAMES.FILTER_CONDITION_ADDED, APPLICATIONS_SUB_FEATURE_IDS.FILTERS, {
            entityName: filterContext,
          }),
        )
    }

    previousFilterIds = currentFilterIds
  },
  { deep: true },
)

const noFilters = computed(() => !publicFilters.value.length && !privateFilters.value.length)
const hasTimeframeFilter = computed(() => {
  return newFilterOptions.value.some(({ option }) => dateConditions.includes(option.name))
})

// In AI mode, show only the date section until a prompt returns filters beyond the injected date.
const hasNonDateOptions = computed(() =>
  newFilterOptions.value.some(({ option }) => !dateConditions.includes(option.name)),
)

const isDefaultOption = (o: DeconstructedOption) =>
  !o.option.isAutoQa && !o.option.isSpotlight && !o.option.isCustomCategory && !o.option.isIntelligent

/* conversation filters should always have time constraint so requests wouldn't take too long */
const getEnhancedFilterOptions = (options: DeconstructedOption[]) => {
  const enhancedOptions = constructFilterOptions(options.filter(isDefaultOption))

  if (!hasTimeframeFilter.value) {
    enhancedOptions.push({ type: 'DEFAULT', option: 'TICKET_CREATED_DATE', condition: 'DATE_30_DAYS' })
  }

  return enhancedOptions
}

watch(
  () => props.showModal,
  (showModal) => {
    if (showModal) {
      previousFilterIds = null
      showFilterModal()
    } else {
      saveClicked.value = false
      // AI results were shown but the user closed without saving.
      if (hasSubmittedPrompt.value && !aiSaved.value) {
        trackEvent(PROMPT_FILTERING_EVENT_NAMES.FILTERS_DISCARDED, APPLICATIONS_SUB_FEATURE_IDS.PROMPT_FILTERING)
      }
    }
  },
)

const selectedAutoQaFilterNames = computed(() =>
  newFilterOptions.value.filter((o) => o.option.isAutoQa).map(({ option }) => option.name?.toLowerCase()),
)

const dateRange = computed(() => {
  const createdDateOption = newFilterOptions.value.find(({ option }) => option.name === 'TICKET_CREATED_DATE')
  if (!createdDateOption) return null

  return getDayjsRangeFromOption(createdDateOption)
})

const { warningMessage } = useAutoQaDateWarning({
  workspaceId: session.workspace.id,
  dateRange,
  usedAutoQaCategories: selectedAutoQaFilterNames,
})

watch(
  newFilterOptions,
  async (options) => {
    if (!options.length) return
    loadingTickets.value = true

    try {
      showTicketCountError.value = false
      const { data } = await memGetFilterTicketCount({
        isPublic: newFilterPublicity.value,
        name: newFilterName.value || 'temporary-name',
        options: getEnhancedFilterOptions(options),
        autoQaOptions: constructFilterOptions(options.filter((o) => o.option.isAutoQa)),
        spotlightOptions: constructFilterOptions(options.filter((o) => o.option.isSpotlight)),
        customCategoryOptions: constructFilterOptions(options.filter((o) => o.option.isCustomCategory)),
        intelligentFilterOptions: constructFilterOptions(options.filter((o) => o.option.isIntelligent)),
      })
      filterTicketCount.value = data
    } catch (err) {
      console.info(err)
      filterTicketCount.value = 0
      showTicketCountError.value = true
    } finally {
      loadingTickets.value = false
    }
  },
  { deep: true },
)

const onAiLoading = (loading: boolean) => {
  aiLoading.value = loading

  // When a new prompt starts loading, reset previously selected filter options so stale results
  // aren't shown. Keep only the date constraint, which every conversation filter requires.
  if (loading) {
    newFilterOptions.value = newFilterOptions.value.filter(({ option }) => dateConditions.includes(option.name))
  }
}

const onAiResults = (results: DeconstructedOption[]) => {
  hasSubmittedPrompt.value = true
  aiResultsSnapshot.value = structuredClone(results)
  newFilterOptions.value = results
}

// Stable serialized form of a set of filter options, so the AI snapshot and the final options can
// be compared regardless of ordering. `id` is excluded — it's a transient client uuid. The sort
// key covers option + condition + values so duplicate option names can't collide or reorder.
const normalizeOptions = (options: DeconstructedOption[]) =>
  JSON.stringify(
    options
      .map(({ option, condition, values }) => ({
        option: option?.name,
        condition: condition?.name,
        values,
      }))
      .sort((a, b) =>
        `${a.option}|${a.condition}|${JSON.stringify(a.values)}`.localeCompare(
          `${b.option}|${b.condition}|${JSON.stringify(b.values)}`,
        ),
      ),
  )

onMounted(() => {
  if (props.promptNewFilter) showFilterModal()
})

onBeforeUnmount(() => {
  editModal.value = false
})

const addFilter = async () => {
  saveClicked.value = true

  if (!newFilterName.value?.length) {
    toast({ status: 'error', message: i18n.t('filter.empty_name_toast') })
    nameFieldRef.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  if (!hasTimeframeFilter.value) {
    toast({ status: 'error', message: i18n.t('filter.date_condition_toast') })
    document.querySelector('[data-testid=date-condition-group]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  const data = {
    isPublic: newFilterPublicity.value,
    name: newFilterName.value,
    options: constructFilterOptions(newFilterOptions.value.filter(isDefaultOption)),
    autoQaOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isAutoQa)),
    spotlightOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isSpotlight)),
    customCategoryOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isCustomCategory)),
    intelligentFilterOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isIntelligent)),
  }

  let filterId: number | undefined = undefined

  if (editModal.value) {
    if (editFilterId.value) {
      filterId = await editFilter({ id: editFilterId.value, data }).then(({ data }) => data)
      emit('update')
    }
  } else {
    filterId = await createFilter(data).then(({ data }) => data)
  }
  if (!filterId) return
  setFilter(filterId)
  trackEvent(APPLICATIONS_EVENT_NAMES.FILTER_SAVED, APPLICATIONS_SUB_FEATURE_IDS.FILTERS, { entityName: filterContext })

  if (hasSubmittedPrompt.value) {
    const appliedAsIs =
      aiResultsSnapshot.value && normalizeOptions(newFilterOptions.value) === normalizeOptions(aiResultsSnapshot.value)
    trackEvent(PROMPT_FILTERING_EVENT_NAMES.FILTERS_APPLIED, APPLICATIONS_SUB_FEATURE_IDS.PROMPT_FILTERING, {
      entityName: appliedAsIs ? 'as-is' : 'modified',
    })
    aiSaved.value = true
  }

  toast({
    status: 'success',
    message: editModal.value
      ? i18n.t('conversations.filters.update_success_toast')
      : i18n.t('conversations.filters.add_success_toast'),
  })
  editModal.value = false
  emit('close')
  newFilterOptions.value = []
  newFilterName.value = ''
  newFilterPublicity.value = false
}

const showFilterModal = async (_?: any, localFilterOptions?: any) => {
  editModal.value = false
  loadingModal.value = true
  hasSubmittedPrompt.value = false
  aiLoading.value = false
  aiSaved.value = false
  aiResultsSnapshot.value = null
  isAiMode.value = !localFilterOptions

  // Track only when the assistant is opened for a new filter — editing an existing filter opens the
  // modal in AI mode too, but that's not an "assistant opened" adoption signal.
  if (isAiMode.value && !props.editableFilter) {
    trackEvent(PROMPT_FILTERING_EVENT_NAMES.ASSISTANT_OPENED, APPLICATIONS_SUB_FEATURE_IDS.PROMPT_FILTERING)
  }

  if (props.editableFilter) {
    editModal.value = true
    editFilterId.value = props.editableFilter.filterId
    newFilterName.value = props.editableFilter.name
    newFilterPublicity.value = props.editableFilter.isPublic
    filterUpdatedInfo.value = getFilterUpdatedInfo(props.editableFilter)

    const filterDetails = await getFilterDetails(props.editableFilter.filterId, false).then(({ data }) => data)

    newFilterOptions.value = deconstructFilterOptions({
      options: filterDetails.options || [],
      autoQaOptions: filterDetails.autoQaOptions || [],
      spotlightOptions: filterDetails.spotlightOptions || [],
      customCategoryOptions: filterDetails.customCategoryOptions || [],
      intelligentFilterOptions: filterDetails.intelligentFilterOptions || [],
      defaultOptions: fetchedFilterOptions.value?.defaultFilterOptions,
      defaultAutoQaOptions: fetchedFilterOptions.value?.autoQaFilterOptions,
      defaultSpotlightOptions: fetchedFilterOptions.value?.spotlightFilterOptions,
      defaultCustomCategoryOptions: fetchedFilterOptions.value?.customCategoryFilterOptions,
      defaultIntelligentFilterOptions: fetchedFilterOptions.value?.intelligentFilterOptions,
      displayAsEmoji: displayAsEmoji.value,
    })
  } else {
    editFilterId.value = null
    newFilterName.value = ''
    newFilterOptions.value = localFilterOptions || [
      {
        id: uuid(),
        option: {
          name: 'TICKET_CREATED_DATE',
          label: 'filter.option.label.ticket_created_date',
        },
        condition: {
          name: 'DATE_30_DAYS',
          label: 'filter.condition.label.date_30_days',
          valueRequired: false,
        },
        values: [],
      },
    ]
    newFilterPublicity.value = props.public
    filterUpdatedInfo.value = undefined
  }
  loadingModal.value = false
  filterTicketCount.value = 0
}

const displayNoFiltersPrompt = async () => {
  const assignedToAgentFilter = constructDynamicFilterOptions({
    forFilter: true,
    options: ['ASSIGNEE'],
    conditions: ['LONG_LIST_EQUALS'],
    values: [1],
  })
  const labelledOptions = fetchedFilterOptions.value?.defaultFilterOptions.length
    ? generateFilterLabels(assignedToAgentFilter, fetchedFilterOptions.value.defaultFilterOptions)
    : []
  await showFilterModal(null, labelledOptions)
  newFilterName.value = 'My tickets'
  bus.$emit('ticket-hide-loader')
}

const getFilterUpdatedInfo = (filter) => {
  const { updated, created, updateByName, ownerName } = filter
  const agentName = updateByName || ownerName || i18n.t('universal.former_member', 2)
  return updated
    ? i18n.t('conversations.filters.form.filter_updated', { time: formatDateIntl(updated || created), agentName })
    : i18n.t('conversations.filters.form.filter_created', { time: formatDateIntl(updated || created), agentName })
}

const setFilter = (id: number | string) => {
  const isNumericFilter = !isNaN(id as number)
  setActiveFilterId(id)
  if (!activeFilterId.value || (isNumericFilter && activeFilterId.value === id)) emit('update')
}

watch(
  () => props.showNoFiltersModal,
  (state) => {
    if (state) {
      displayNoFiltersPrompt()
      emit('stop-ticket-list-loading')
    }
  },
  { immediate: true },
)
</script>
